import { useState, useEffect } from "react"
import axios from "axios"
import base64 from "base-64"

import {
	FarmGirlI,
	RawFarmGirlURIMetadata,
} from "@hfc/shared/typings/game/FarmGirl"
import FarmGirls from "@hfc/shared/utils/blockchain/contracts/FarmGirls"
import getBlockchainConfig from "@hfc/shared/config/web3/getBlockchainConfig"

import { providers, Signer } from "ethers"

const api = "/api/getUserNFTCollectionIds"

const getFarmGirlMetadata = async (
	id: number,
	signerOrProvider: Signer | providers.Provider,
) => {
	const farmGirls = new FarmGirls(
		getBlockchainConfig().farmGirlAddress,
		signerOrProvider,
	)
	const uri = await farmGirls.uri(id)

	const metadata: RawFarmGirlURIMetadata = uri
		? JSON.parse(
				base64.decode(uri.replace("data:application/json;base64,", "")),
		  )
		: {}

	const { name, description } = metadata

	const image =
		metadata?.image?.replace(
			"https://ipfs.io",
			"https://gateway.pinata.cloud",
		) ?? ""

	const traits =
		metadata?.attributes?.map(({ trait_type, value, max_value }) => ({
			type: trait_type,
			value,
			maxValue: max_value,
		})) ?? []

	const {
		// Visuals
		Background,
		Clothing,
		Mouth,
		Hair,
		Eyes,
		Earrings,
	} = traits.reduce((acc, { type, value }) => {
		return {
			...acc,
			[type]: value,
		}
	}, {} as { [traitType: string]: string })

	const farmer: FarmGirlI & any = {
		objectID: id,
		id: Number(id),
		name,
		description,

		// Visuals
		image,

		// Traits
		Background,
		Clothing,
		Mouth,
		Hair,
		Eyes,
		Earrings,
	}

	return farmer
}

const useFarmGirls = (
	account: string,
	signerOrProvider: Signer | providers.Provider,
) => {
	const [tokenIds, setTokenIds] = useState<number[]>([])
	const [stakedFarmGirlIds, setStakedFarmGirlIds] = useState<number[]>([])
	const [farmGirls, setFarmGirls] = useState<FarmGirlI[]>([])
	const [isLoading, setIsLoading] = useState<boolean>(false)

	useEffect(() => {
		const fetchUserFarmerIds = async (account: string) => {
			setIsLoading(true)
			const { data } = (await axios.get(
				`${api}?account=${account}&contract=${getBlockchainConfig().farmGirlAddress?.toLowerCase()}&useTheFarmV2=true`,
			)) as {
				data: {
					userTokenIds: number[]
					farmGirlIdsInWallet: number[]
					stakedFarmerIds: number[]
				}
			}

			const farmGirls = await Promise.all(
				data?.userTokenIds?.map((id) => {
					return getFarmGirlMetadata(id, signerOrProvider)
				}),
			)

			return { ...data, farmGirls }
		}

		if (account) {
			fetchUserFarmerIds(account).then(
				({ userTokenIds, stakedFarmerIds, farmGirls }) => {
					setIsLoading(false)
					setTokenIds(userTokenIds)
					setStakedFarmGirlIds(stakedFarmerIds)
					setFarmGirls(farmGirls)
					// refetch()
				},
			)
		}
	}, [account])

	return {
		farmGirls,
		tokenIds,
		stakedFarmGirlIds,
		isLoading,
	}
}

export default useFarmGirls
